import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { audienceListsApi } from "@/api";
import type { AudienceListMemberStatus } from "@/types";
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCcw, Search, Send, UserMinus, UserPlus } from "lucide-react";
import { format } from "date-fns";

type ContactAddInput = {
  email: string;
  company?: string;
  lastName?: string;
  firstName?: string;
};

const emptyContactForm: ContactAddInput = {
  email: "",
  company: "",
  lastName: "",
  firstName: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function textOrUndefined(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function cleanContactInput(input: ContactAddInput): ContactAddInput | null {
  const email = input.email.trim().toLowerCase();
  if (!emailPattern.test(email)) return null;
  return {
    email,
    company: textOrUndefined(input.company),
    lastName: textOrUndefined(input.lastName),
    firstName: textOrUndefined(input.firstName),
  };
}

function parseBulkContacts(text: string): ContactAddInput[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line) => {
      const separator = line.includes("\t") ? "\t" : line.includes(",") ? "," : /\s+/;
      const parts = line
        .split(separator)
        .map((part) => part.trim());
      if (parts.every((part) => emailPattern.test(part.toLowerCase()))) {
        return parts
          .map((email) => cleanContactInput({ email }))
          .filter((contact): contact is ContactAddInput => !!contact);
      }
      const emailIndex = parts.findIndex((part) => emailPattern.test(part.toLowerCase()));
      if (emailIndex === -1) return [];
      if (emailIndex === 0) {
        return cleanContactInput({
          email: parts[0],
          company: parts[1],
          lastName: parts[2],
          firstName: parts[3],
        });
      }
      return cleanContactInput({
        company: parts[0],
        lastName: parts[1],
        firstName: parts[2],
        email: parts[emailIndex],
      });
    })
    .filter((contact): contact is ContactAddInput => !!contact);
}

export default function AudienceListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AudienceListMemberStatus>("INCLUDED");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [contactForm, setContactForm] = useState<ContactAddInput>(emptyContactForm);
  const [bulkContactText, setBulkContactText] = useState("");

  const { data: list } = useQuery({
    queryKey: ["audience-list", id],
    queryFn: () => audienceListsApi.get(id!),
    enabled: !!id,
  });

  const { data: membersData, isLoading } = useQuery({
    queryKey: ["audience-list-members", id, status, q, page],
    queryFn: () => audienceListsApi.members(id!, { status, q, page: String(page), limit: "100" }),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      includeIds?: string[];
      includeEmails?: string[];
      includeContacts?: ContactAddInput[];
      excludeIds?: string[];
      restoreIds?: string[];
      note?: string;
    }) =>
      audienceListsApi.updateMembers(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audience-list", id] });
      queryClient.invalidateQueries({ queryKey: ["audience-list-members", id] });
      queryClient.invalidateQueries({ queryKey: ["audience-lists"] });
    },
  });

  const members = membersData?.data ?? [];
  const totalPages = membersData?.totalPages ?? 1;
  const total = membersData?.total ?? 0;
  const singleContactToAdd = cleanContactInput(contactForm);
  const contactsToAdd = [
    ...(singleContactToAdd ? [singleContactToAdd] : []),
    ...parseBulkContacts(bulkContactText),
  ];
  const canAddContacts = contactsToAdd.length > 0 && !updateMutation.isPending;
  const addContacts = () => {
    if (!canAddContacts) return;
    updateMutation.mutate(
      { includeContacts: contactsToAdd, note: "manual contact add" },
      {
        onSuccess: () => {
          setContactForm(emptyContactForm);
          setBulkContactText("");
        },
      },
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button
        onClick={() => navigate("/audience-lists")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        <ArrowLeft size={16} />
        宛先リスト一覧に戻る
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{list?.name ?? "宛先リスト"}</h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-slate-500">
            <span>{list?.mode}</span>
            <span>対象 {list?.eligibleCount?.toLocaleString() ?? list?.lastCount?.toLocaleString() ?? "-"} 件</span>
            {list?.updatedAt && <span>更新 {format(new Date(list.updatedAt), "yyyy/MM/dd HH:mm")}</span>}
          </div>
        </div>
        {list && (
          <button
            type="button"
            onClick={() => navigate(`/campaigns/new?audienceListId=${encodeURIComponent(list.id)}`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800"
          >
            <Send size={16} />
            このリストに配信を作成
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-slate-900">宛先を追加</h2>
          <button
            type="button"
            onClick={addContacts}
            disabled={!canAddContacts}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
          >
            <UserPlus size={16} />
            {contactsToAdd.length ? `${contactsToAdd.length}件追加` : "追加"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_0.8fr_0.8fr_1.4fr] gap-2 mb-3">
          <input
            type="text"
            value={contactForm.company}
            onChange={(e) => setContactForm((current) => ({ ...current, company: e.target.value }))}
            placeholder="会社名"
            className="px-3 py-2 border rounded-md text-sm"
          />
          <input
            type="text"
            value={contactForm.lastName}
            onChange={(e) => setContactForm((current) => ({ ...current, lastName: e.target.value }))}
            placeholder="姓"
            className="px-3 py-2 border rounded-md text-sm"
          />
          <input
            type="text"
            value={contactForm.firstName}
            onChange={(e) => setContactForm((current) => ({ ...current, firstName: e.target.value }))}
            placeholder="名"
            className="px-3 py-2 border rounded-md text-sm"
          />
          <input
            type="email"
            value={contactForm.email}
            onChange={(e) => setContactForm((current) => ({ ...current, email: e.target.value }))}
            placeholder="メールアドレス"
            className="px-3 py-2 border rounded-md text-sm"
          />
        </div>
        <textarea
          value={bulkContactText}
          onChange={(e) => setBulkContactText(e.target.value)}
          placeholder="まとめて追加: メール,会社名,姓,名 / 会社名,姓,名,メール"
          className="w-full px-3 py-2 border rounded-md text-sm min-h-20"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="p-4 border-b flex items-center gap-3">
          <div className="inline-flex rounded-md border overflow-hidden">
            <button
              type="button"
              onClick={() => {
                setStatus("INCLUDED");
                setPage(1);
              }}
              className={`px-3 py-1.5 text-sm ${status === "INCLUDED" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}
            >
              対象
            </button>
            <button
              type="button"
              onClick={() => {
                setStatus("EXCLUDED");
                setPage(1);
              }}
              className={`px-3 py-1.5 text-sm border-l ${status === "EXCLUDED" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}
            >
              除外済み
            </button>
          </div>
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="会社名・名前・メールで検索"
              className="w-full pl-8 pr-3 py-2 border rounded-md text-sm"
            />
          </div>
          <span className="text-sm text-slate-500">{total.toLocaleString()} 件</span>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-slate-500">読み込み中...</div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">会社名</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">名前</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">メール</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">種別</th>
                  <th className="text-right text-xs font-medium text-slate-500 px-4 py-3">操作</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const contact = member.contact;
                  if (!contact) return null;
                  return (
                    <tr key={member.id} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm">{contact.company || "-"}</td>
                      <td className="px-4 py-3 text-sm">{[contact.lastName, contact.firstName].filter(Boolean).join(" ") || "-"}</td>
                      <td className="px-4 py-3 text-sm">{contact.email}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{contact.traderType || "-"}</td>
                      <td className="px-4 py-3 text-right">
                        {status === "INCLUDED" ? (
                          <button
                            type="button"
                            onClick={() => updateMutation.mutate({ excludeIds: [contact.id], note: "manual exclude" })}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-600 hover:bg-red-50"
                          >
                            <UserMinus size={14} />
                            除外
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => updateMutation.mutate({ restoreIds: [contact.id], note: "manual restore" })}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-blue-600 hover:bg-blue-50"
                          >
                            <RotateCcw size={14} />
                            復活
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {membersData && totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
                <p className="text-sm text-slate-500">
                  {(page - 1) * membersData.limit + 1}-{Math.min(page * membersData.limit, total)} / {total}件
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    disabled={page <= 1}
                    className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                    title="前へ"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-slate-500">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                    disabled={page >= totalPages}
                    className="p-2 border rounded-md hover:bg-slate-50 disabled:opacity-50"
                    title="次へ"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
